import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { MdClose, MdEdit, MdChevronLeft, MdChevronRight } from "react-icons/md";
import axios from "axios";

const ExecutiveHistory = ({ executiveId }) => {
  const [positiveCalls, setPositiveCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [showPopup, setShowPopup] = useState(false);
  const [selectedCall, setSelectedCall] = useState(null);
  const [editedCallStatus, setEditedCallStatus] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState(null);

  const callsPerPage = 8;

  useEffect(() => {
    fetchPositiveCalls();
  }, [executiveId]);

  const fetchPositiveCalls = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_API}/api/positive-calls/executive/${executiveId}/positive-calls`
      );
      setPositiveCalls(response.data.data);
    } catch (error) {
      setError("Failed to load calls. Please try again.");
      console.error("Error fetching positive calls:", error);
    } finally {
      setLoading(false);
    }
  };

  // Pagination logic
  const indexOfLastCall = currentPage * callsPerPage;
  const indexOfFirstCall = indexOfLastCall - callsPerPage;
  const currentCalls = positiveCalls.slice(indexOfFirstCall, indexOfLastCall);
  const totalPages = Math.ceil(positiveCalls.length / callsPerPage);

  const paginate = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const openPopup = (call) => {
    setSelectedCall(call);
    setEditedCallStatus(call.callStatus || ""); // Default to blank for callStatus
    setDescription(call.description || "");
    setShowPopup(true);
  };

  const closePopup = () => {
    setShowPopup(false);
    setSelectedCall(null);
    setError(null);
  };

  const saveChanges = async () => {
    try {
      setError(null);
      const updatedData = {
        callStatus: editedCallStatus, // Only update callStatus (not executiveStatus)
        description: description,
      };

      const response = await axios.put(
        `${process.env.REACT_APP_SERVER_API}/api/positive-calls/call/${selectedCall._id}`,
        updatedData
      );

      setPositiveCalls(
        positiveCalls.map((call) =>
          call._id === selectedCall._id ? response.data.data : call
        )
      );
      fetchPositiveCalls();
      closePopup();
    } catch (error) {
      setError("Failed to save changes. Please try again.");
      console.error("Error updating call:", error);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 min-h-screen bg-gray-50">
      <h1 className="text-3xl font-bold mb-8 text-gray-800 text-center">
        Executive Call History
      </h1>

      {error && (
        <div className="mb-6 p-4 bg-red-50 text-red-600 rounded-lg flex justify-between items-center shadow-sm">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-800">
            <MdClose />
          </button>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gradient-to-r from-indigo-600 to-indigo-800 text-white">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Sr No</th>
                <th className="px-4 py-3 text-left font-semibold">Executive Mobile</th>
                <th className="px-4 py-3 text-left font-semibold">Agent Name</th>
                <th className="px-4 py-3 text-left font-semibold">Customer Name</th>
                <th className="px-4 py-3 text-left font-semibold">Customer Mobile</th>
                <th className="px-4 py-3 text-left font-semibold">Call Status</th>
                <th className="px-4 py-3 text-left font-semibold">Executive Status</th>
                <th className="px-4 py-3 text-left font-semibold">Description</th>
                <th className="px-4 py-3 text-left font-semibold">Action</th>
              </tr>
            </thead>
            <tbody>
              {currentCalls.length === 0 ? (
                <tr>
                  <td colSpan="9" className="px-4 py-4 text-center text-gray-500">
                    No calls found
                  </td>
                </tr>
              ) : (
                currentCalls.map((call, index) => (
                  <motion.tr
                    key={call._id}
                    className="border-b hover:bg-gray-100 transition-colors duration-200"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    <td className="px-4 py-3 text-gray-700">
                      {indexOfFirstCall + index + 1}
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {call.executiveMobileNumber || "N/A"}
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {call.agentId?.name || "Unknown Agent"}
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {call.crmContactId?.name || "Unknown Customer"}
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {call.crmContactId?.mobileNumber || "N/A"}
                    </td>
                    {/* Call Status column displays executiveStatus */}
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                          call.executiveStatus === "Positive"
                            ? "bg-green-100 text-green-700"
                            : call.executiveStatus === "Negative"
                            ? "bg-red-100 text-red-700"
                            : "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {call.executiveStatus || "N/A"}
                      </span>
                    </td>
                    {/* Executive Status column displays callStatus */}
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                          call.callStatus === "positive"
                            ? "bg-blue-100 text-blue-700"
                            : call.callStatus === "negative"
                            ? "bg-red-100 text-red-700"
                             : call.callStatus === "negativeDND"
                            ? "bg-red-100 text-red-700"
                            : call.callStatus === "Neutral"
                            ? "bg-yellow-100 text-yellow-700"
                            : "bg-gray-100 text-gray-700"
                            
                        }`}
                      >
                        {call.callStatus || "N/A"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {call.description || "N/A"}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => openPopup(call)}
                        className="flex items-center px-3 py-1 bg-indigo-500 text-white rounded-md hover:bg-indigo-600 transition-colors duration-200"
                      >
                        <MdEdit className="mr-1" /> Edit
                      </button>
                    </td>
                  </motion.tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-between items-center mt-6 px-4">
          <button
            onClick={() => paginate(currentPage - 1)}
            disabled={currentPage === 1}
            className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
              currentPage === 1
                ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                : "bg-indigo-600 text-white hover:bg-indigo-700"
            }`}
          >
            <MdChevronLeft className="mr-1" /> Previous
          </button>

          <div className="flex space-x-2">
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i}
                onClick={() => paginate(i + 1)}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-all duration-200 ${
                  currentPage === i + 1
                    ? "bg-indigo-600 text-white shadow-md"
                    : "bg-gray-200 text-gray-700 hover:bg-indigo-100"
                }`}
              >
                {i + 1}
              </button>
            ))}
          </div>

          <button
            onClick={() => paginate(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
              currentPage === totalPages
                ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                : "bg-indigo-600 text-white hover:bg-indigo-700"
            }`}
          >
            Next <MdChevronRight className="ml-1" />
          </button>
        </div>
      )}

      {/* Popup */}
      {showPopup && (
        <motion.div
          className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="bg-white rounded-xl p-6 w-full max-w-sm shadow-xl"
            initial={{ scale: 0.9, y: -20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-800">
                Executive Update
              </h2>
              <button
                onClick={closePopup}
                className="text-gray-500 hover:text-red-600 transition-colors duration-200"
              >
                <MdClose className="text-xl" />
              </button>
            </div>

            {error && (
              <div className="mb-4 p-2 bg-red-50 text-red-600 rounded-md text-sm">
                {error}
              </div>
            )}

            <div className="space-y-4">
              <div>
                {/* Heading says "Executive Status" but edits callStatus */}
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Executive Status
                </label>
                <select
                  value={editedCallStatus}
                  onChange={(e) => setEditedCallStatus(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200"
                >
                  <option value="">Select Status</option>
                  <option value="positive">Positive</option>
                  <option value="negative">Negative</option>
                  <option value="negativeDND">negativeDND</option>
                  <option value="Neutral">Neutral</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 h-24 resize-none transition-all duration-200"
                  placeholder="Enter description..."
                />
              </div>

              <div className="flex justify-end space-x-2">
                <button
                  onClick={closePopup}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={saveChanges}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors duration-200"
                >
                  Update
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default ExecutiveHistory;